const PENDING_LIVENESS_CALLBACK_STATE_STORAGE_KEY = 'auth_pending_liveness_callback_state';
const PENDING_LIVENESS_CALLBACK_STATE_TTL_MS = 10 * 60 * 1_000;

type PendingLivenessCallbackState = {
  callbackState: string;
  pendingLivenessAt: number | null;
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
};

const readPendingLivenessCallbackState = (): PendingLivenessCallbackState | null => {
  const storedValue = getLocalStorage()?.getItem(PENDING_LIVENESS_CALLBACK_STATE_STORAGE_KEY);

  if (!storedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue) as Partial<PendingLivenessCallbackState>;

    if (typeof parsedValue.callbackState !== 'string') {
      return null;
    }

    return {
      callbackState: parsedValue.callbackState,
      pendingLivenessAt:
        typeof parsedValue.pendingLivenessAt === 'number' ? parsedValue.pendingLivenessAt : null,
    };
  } catch {
    return null;
  }
};

const writePendingLivenessCallbackState = (state: PendingLivenessCallbackState) => {
  try {
    getLocalStorage()?.setItem(PENDING_LIVENESS_CALLBACK_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (storageError) {
    console.warn('[AuthCallback] Failed to store pending liveness callback state.', storageError);
  }
};

export const rememberAuthCallbackStateForPendingLiveness = (callbackState: string | null) => {
  if (callbackState === null) {
    return;
  }

  const storedState = readPendingLivenessCallbackState();

  writePendingLivenessCallbackState({
    callbackState,
    pendingLivenessAt:
      storedState?.callbackState === callbackState ? storedState.pendingLivenessAt : null,
  });
};

export const markPendingLivenessDuringAuthCallback = () => {
  const storedState = readPendingLivenessCallbackState();

  if (!storedState) {
    return;
  }

  writePendingLivenessCallbackState({
    ...storedState,
    pendingLivenessAt: Date.now(),
  });
};

export const clearPendingLivenessCallbackState = () => {
  getLocalStorage()?.removeItem(PENDING_LIVENESS_CALLBACK_STATE_STORAGE_KEY);
};

export const hasPendingLivenessCallbackState = (callbackState: string | null) => {
  return getPendingLivenessCallbackStartedAt(callbackState) !== null;
};

export const getPendingLivenessCallbackStartedAt = (callbackState: string | null) => {
  if (callbackState === null) {
    return null;
  }

  const storedState = readPendingLivenessCallbackState();

  if (
    !storedState ||
    storedState.callbackState !== callbackState ||
    storedState.pendingLivenessAt === null
  ) {
    return null;
  }

  if (Date.now() - storedState.pendingLivenessAt > PENDING_LIVENESS_CALLBACK_STATE_TTL_MS) {
    return null;
  }

  return storedState.pendingLivenessAt;
};
